import { useMemo } from "react";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { TYPOGRAPHY_KEYS, type TypographyStyles } from "./typographyTypes";
import { useCurrentBlockTypography } from "./useCurrentBlockTypography";

const TYPOGRAPHY_KEY_TO_CSS_SUFFIX: Record<keyof TypographyStyles, string> = {
  fontFamily: "font-family",
  fontStyle: "font-style",
  fontWeight: "font-weight",
};

/**
 * Hook to extract the theme typography values for the current block type.
 * This reads the actual resolved values from the theme.
 * Used for applying base typography and calculating dynamic bold weights.
 */
export const useEditorTypography = (): TypographyStyles => {
  const typographyLevel = useCurrentBlockTypography();
  const currentCanvasTheme = useBuilderStore(state => state.currentCanvasTheme);

  return useMemo(() => {
    const typography: TypographyStyles = {};

    if (!currentCanvasTheme?.cssVariables) {
      return typography;
    }

    const cssVars = currentCanvasTheme.cssVariables;
    const categoryVars = cssVars[typographyLevel];

    if (categoryVars && typeof categoryVars === "object") {
      for (const key of TYPOGRAPHY_KEYS) {
        const cssVarKey = `theme-email-${typographyLevel}-${TYPOGRAPHY_KEY_TO_CSS_SUFFIX[key]}`;
        const value = categoryVars[cssVarKey];
        if (value != undefined) {
          typography[key] = String(value);
        }
      }
    }

    return typography;
  }, [typographyLevel, currentCanvasTheme]);
};
